import { useState } from 'react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { LcPoolSummary } from '../hooks/useLiquidityCachePools';
import { api } from '@/lib/api';

interface InvestPanelProps {
  pool: LcPoolSummary;
  onInvestComplete: () => void;
}

export function InvestPanel({ pool, onInvestComplete }: InvestPanelProps) {
  const [tokenAAmount, setTokenAAmount] = useState('');
  const [tokenBAmount, setTokenBAmount] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const handleInvest = async () => {
    if (!tokenAAmount || !tokenBAmount) return;
    setSubmitting(true);
    setError(null);
    try {
      await api.liquidityCacheInvest(pool.poolId, { tokenAAmount, tokenBAmount });
      setTokenAAmount('');
      setTokenBAmount('');
      onInvestComplete();
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Investment failed');
    } finally {
      setSubmitting(false);
    }
  };

  // Estimate LP shares preview
  const previewShares = (() => {
    try {
      const a = BigInt(tokenAAmount || '0');
      const b = BigInt(tokenBAmount || '0');
      const rA = BigInt(pool.reserveA);
      const rB = BigInt(pool.reserveB);
      const totalShares = BigInt(pool.totalLpShares);
      if (a <= 0n || b <= 0n) return null;
      if (totalShares === 0n) return 'First deposit — shares = sqrt(A*B) - minLiquidity';
      const sharesA = (a * totalShares) / rA;
      const sharesB = (b * totalShares) / rB;
      const shares = sharesA < sharesB ? sharesA : sharesB;
      return `~${shares.toString()} LP shares`;
    } catch {
      return null;
    }
  })();

  return (
    <div className="space-y-4">
      <div>
        <Label>{pool.tokenA.symbol} Amount</Label>
        <Input
          type="text"
          value={tokenAAmount}
          onChange={(e) => setTokenAAmount(e.target.value)}
          placeholder="0"
        />
      </div>

      <div>
        <Label>{pool.tokenB.symbol} Amount</Label>
        <Input
          type="text"
          value={tokenBAmount}
          onChange={(e) => setTokenBAmount(e.target.value)}
          placeholder="0"
        />
      </div>

      {previewShares && <p className="text-sm text-muted-foreground">Estimated: {previewShares}</p>}

      {error && <p className="text-sm text-red-600">{error}</p>}

      <Button
        onClick={handleInvest}
        disabled={!tokenAAmount || !tokenBAmount || submitting}
        className="w-full"
      >
        {submitting ? 'Investing...' : 'Invest'}
      </Button>
    </div>
  );
}
